<template>
  <UForm :schema="schema" :state="state" class="space-y-4 p-4 w-full" @submit="onSubmit">
    <div class="flex flex-col sm:flex-row gap-4">
      <UFormField label="Network" name="network" class="flex-1">
        <USelect
          v-model="state.network"
          :items="networks"
          class="w-full"
          placeholder="Select a network"
        />
      </UFormField>

      <UFormField label="Site Code" name="siteCode" class="flex-3">
        <USelect
          v-model="state.siteCode"
          :items="siteData"
          class="w-full"
          :disabled="!siteData"
          placeholder="Select a site"
          :loading="isLoading"
          loading-icon="i-lucide-loader"
        />
      </UFormField>

      <UFormField label="Species" name="species" class="flex-1">
        <USelect v-model="state.species" :items="species" class="w-full" />
      </UFormField>

      <UFormField label="Output" name="output" class="flex-1">
        <USelect v-model="state.output" :items="outputs" class="w-full" />
      </UFormField>
    </div>

    <div class="flex flex-col sm:flex-row gap-4">
      <UFormField label="Start Date" name="startDate" class="flex-1">
        <UPopover>
          <UButton
            color="neutral"
            variant="subtle"
            icon="i-lucide-calendar"
            class="w-full justify-start"
          >
            {{
              state.startDate
                ? df.format(state.startDate.toDate(getLocalTimeZone()))
                : "Select a date"
            }}
          </UButton>

          <template #content>
            <UCalendar v-model="state.startDate" class="p-2" />
          </template>
        </UPopover>
      </UFormField>

      <UFormField label="End Date" name="endDate" class="flex-1">
        <UPopover>
          <UButton
            color="neutral"
            variant="subtle"
            icon="i-lucide-calendar"
            class="w-full justify-start"
          >
            {{
              state.endDate ? df.format(state.endDate.toDate(getLocalTimeZone())) : "Select a date"
            }}
          </UButton>

          <template #content>
            <UCalendar v-model="state.endDate" class="p-2" />
          </template>
        </UPopover>
      </UFormField>
    </div>

    <div class="flex flex-row gap-4">
      <UFormField label="Chart Type" name="chartType" class="w-1/4">
        <USelect v-model="state.chartType" :items="chartTypes" class="w-full" />
      </UFormField>

      <UFormField v-if="state.chartType === 'line'" label="Show Thresholds" name="showThresholds">
        <USwitch v-model="state.showThresholds" class="mt-2" />
      </UFormField>
    </div>

    <div class="flex justify-center gap-2">
      <UButton
        icon="i-lucide-rocket"
        color="neutral"
        variant="outline"
        class="cursor-pointer"
        type="submit"
      >
        Generate
      </UButton>
      <UButton
        icon="i-lucide-save"
        color="neutral"
        variant="outline"
        class="cursor-pointer"
        type="button"
        @click="showModal = true"
      >
        Get Embed Code
      </UButton>
    </div>
  </UForm>
  <UModal v-model:open="showModal" title="Embed Code" close-icon="i-lucide-x" size="xl">
    <template #body>
      <div class="space-y-4">
        <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <pre class="whitespace-pre-wrap break-words text-sm">{{ embedCode }}</pre>
        </div>
        <div class="flex justify-end">
          <UButton
            icon="i-lucide-copy"
            color="primary"
            variant="solid"
            class="cursor-pointer"
            type="button"
            @click="copyEmbedCode"
          >
            Copy
          </UButton>
        </div>
      </div>
    </template>
  </UModal>
</template>

<script setup lang="ts">
import { DateFormatter, getLocalTimeZone, today, parseDate } from "@internationalized/date";
import { boolean, object, string } from "yup";
import { networks, species, outputs, chartTypes } from "@/utils/select-items";

const showModal = ref(false);

const df = new DateFormatter("en-US", {
  dateStyle: "medium",
});

const route = useRoute();
const router = useRouter();
const toast = useToast();

const { fetchSiteData, siteData, isLoading } = useGetApiSiteData();

const embedCode = `<!DOCTYPE html> 
<html>
<head>
  <title>ERG Chart Preview</title>
  <script src="my-plugin.js" onload="window.ERG({ id: 'test-id', className: 'erg-class'});"><\/script>
</head>
<body>
</body>
</html>`;

const schema = object({
  network: string().required("Required"),
  species: string().required("Required"),
  output: string().required("Required"),
  chartType: string().required("Required"),
  showThresholds: boolean().required("Required"),
});

const state = reactive({
  network: route.query.network as string | undefined,
  species: route.query.species as string | undefined,
  siteCode: route.query.siteCode as string | undefined,
  output: route.query.output as string | undefined,
  chartType: route.query.chartType as string | undefined,
  showThresholds: route.query.showThresholds === "true" ? true : false,
  startDate: route.query.startDate
    ? parseDate(route.query.startDate as string)
    : today(getLocalTimeZone()),
  endDate: route.query.endDate
    ? parseDate(route.query.endDate as string)
    : today(getLocalTimeZone()),
});

async function onSubmit() {
  updateRoute({
    network: state.network!,
    species: state.species!,
    output: state.output!,
    siteCode: state.siteCode!,
    showThresholds: state.showThresholds.toString(),
    startDate: state.startDate.toDate(getLocalTimeZone()).toLocaleDateString("en-CA"),
    endDate: state.endDate.toDate(getLocalTimeZone()).toLocaleDateString("en-CA"),
    chartType: state.chartType!,
  });
}

const updateRoute = (query: Record<string, string>) => {
  const newQuery = { ...route.query, ...query };
  router.push({ query: newQuery });
};

watch(
  () => state.network,
  async (_network: string | undefined) => {
    if (!_network) return;
    await fetchSiteData(_network);
  },
  {
    immediate: true,
  },
);

watch(
  () => state.chartType,
  (chartType: string | undefined) => {
    if (chartType) {
      if (chartType === "bar") {
        state.showThresholds = false;
      }

      updateRoute({ chartType });
    }
  },
);

watch(
  () => state.showThresholds,
  (_showThresholds: boolean) => {
    updateRoute({ showThresholds: _showThresholds.toString() });
  },
);

const copyEmbedCode = () => {
  navigator.clipboard.writeText(embedCode);
  toast.add({
    title: "Embed code copied to clipboard",
    color: "success",
  });
};
</script>
